<template>
  <div class="activities-benchmarks">
    <AppPopover
      ref="widgetPopover"
      class="widget-btn-popover"
      :trigger="null"
      @hide="onHideBenchmarksPopover"
    >
      <template slot="body">
        <div class="benchmarks-popover">
          <h5 class="my-5">Activity Benchmarks</h5>
          <p class="mb-0">{{ tooltipTitle }}</p>
        </div>
      </template>

      <template slot="trigger">
        <AppButton
          v-tooltip="{ placement: 'top', html: true }"
          class="widget-btn p-0 pl-5"
          type="naked"
          :title="tooltipTitle"
          @click="openBenchmarksDialog"
        >
          <i class="hi hi-activity pr-10" />
          {{ buttonText }}
        </AppButton>
      </template>
    </AppPopover>
    <div
      class="widget-activities-benchmarks-wrapper"
      :class="`widgets-${totalWidgetsAvailable}`"
    >
      <div class="widget-activities-benchmarks widget-wrapper">
        <div class="widget-wrapper-columns widget-panels-xl">
          <ActivitiesBenchmarksPanel panel-key="time" />
          <ActivitiesBenchmarksPanel
            class="border-left pl-20"
            panel-key="activity"
          />
        </div>
      </div>

      <div
        v-show="totalWidgetsAvailable"
        class="widget-activities-benchmarks widget-wrapper flex-column flex-grow"
      >
        <div class="brand">
          <i class="hi hi-insights"></i>
          Insights
        </div>
        <div
          class="widget-wrapper-columns widget-panels-lg widget-panels-insights"
          :class="`panels-${totalWidgetsAvailable}`"
        >
          <template v-if="insightsAvailable">
            <InsightsFocusTimeWidget
              v-show="showFocusTimeWidget"
              title="Focus Time"
              :chart-size="72"
              :chart-radius="30"
            />
            <InsightsUnusualActivityWidgetScreenshots
              v-if="showUnusualActivityWidget"
              :key="unusualActivityKey"
              :expanded="false"
              :time-zone="timeZone"
            />
            <InsightsMeetingsWidget
              v-show="showMeetingsWidget"
              :expanded="false"
            />
            <InsightsWorkTimeClassificationWidget
              v-show="showWorkTimeClassificationWidget"
              :chart-size="72"
              :chart-radius="30"
            />
          </template>
          <template v-else>
            <ActivitiesBenchmarksInsightsUnusualActivitiesDemo
              :insights-url="insightsUrl('unusual_activity_instances')"
            />
            <ActivitiesBenchmarksInsightsFocusTimeDemo
              :insights-url="insightsUrl('focus_time')"
            />
            <ActivitiesBenchmarksInsightsWorkTimeClassificationDemo
              :insights-url="insightsUrl('work_time_classification')"
            />
          </template>
        </div>
        <a
          v-if="showInsightsLink"
          v-track:click="viewAllTrack"
          :href="url()"
          class="action-link"
          :class="linkClass"
        >
          {{ linkText }}
        </a>
      </div>
    </div>
    <ActivitiesBenchmarksDialog
      :show="showBenchmarksDialog"
      @close="closeBenchmarksDialog"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { formatInternalDate } from '~/utils/time_utils';
import { InsightsResource } from '~/resources';
import { INSIGHTS as INSIGHTS_CONSTANTS, INSIGHTS_ASSETS as INSIGHTS } from '~/app_constants';
import { createPersist } from '~/utils/persist';
import { get } from 'lodash-es';
import ActivitiesBenchmarksPanel from './ActivitiesBenchmarksPanel.vue';
import ActivitiesBenchmarksDialog from './ActivitiesBenchmarksDialog.vue';
import ActivitiesBenchmarksInsightsUnusualActivitiesDemo from './ActivitiesBenchmarksInsightsUnusualActivitiesDemo.vue';
import ActivitiesBenchmarksInsightsFocusTimeDemo from './ActivitiesBenchmarksInsightsFocusTimeDemo.vue';
import ActivitiesBenchmarksInsightsWorkTimeClassificationDemo from './ActivitiesBenchmarksInsightsWorkTimeClassificationDemo.vue';
import InsightsFocusTimeWidget from '~/components/insights/InsightsFocusTimeWidget.vue';
import InsightsMeetingsWidget from '~/components/insights/InsightsMeetingsWidget.vue';
import InsightsWorkTimeClassificationWidget from '~/components/insights/InsightsWorkTimeClassificationWidget.vue';
import InsightsUnusualActivityWidgetScreenshots from '~/components/insights/InsightsUnusualActivityWidgetScreenshots.vue';

const localStore = createPersist('benchmarks');

export default {
  name: 'ActivitiesBenchmarks',
  components: {
    ActivitiesBenchmarksPanel,
    ActivitiesBenchmarksDialog,
    InsightsFocusTimeWidget,
    InsightsMeetingsWidget,
    InsightsUnusualActivityWidgetScreenshots,
    InsightsWorkTimeClassificationWidget,
    ActivitiesBenchmarksInsightsUnusualActivitiesDemo,
    ActivitiesBenchmarksInsightsFocusTimeDemo,
    ActivitiesBenchmarksInsightsWorkTimeClassificationDemo,
  },
  props: {
    showingOverlay: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    INSIGHTS_CONSTANTS,
    showBenchmarksDialog: false,
    showBenchmarksPopover: false,
    viewAllTrack: Object.freeze({ label: 'View all selected' }),
  }),
  computed: {
    ...mapState(['loading', 'organization']),
    ...mapState({
      canViewUnusualActivities: 'can_view_unusual_activities',
    }),
    ...mapGetters([
      'hasSeenBenchmarksDialog',
      'insightsAvailable',
      'canManageSettings',
      'focusTime',
      'workTimeClassification',
      'meetings',
      'queryFilters',
      'queryDate',
      'currentZone',
    ]),
    logo() {
      return INSIGHTS.logo;
    },
    timeZone() {
      return this.currentZone.name;
    },
    tooltipTitle() {
      return "Review how activity works, and compare your organization's activity to others in your industry";
    },
    buttonText() {
      return this.hasSeenBenchmarksDialog ? 'Activity benchmarks' : 'How activity works';
    },
    userId() {
      return this.queryFilters?.user;
    },
    unusualActivityKey() {
      return `${this.queryDate}-${this.userId}-${this.timeZone}`;
    },
    showInsightsLink() {
      return this.canManageSettings || (!this.canManageSettings && this.insightsAvailable);
    },
    linkText() {
      return this.insightsAvailable ? 'View all' : 'Get add-on';
    },
    linkClass() {
      return this.insightsAvailable ? 'right' : 'center';
    },
    totalWidgetsAvailable() {
      if (!this.insightsAvailable) {
        return 0;
      }

      return this.insightsAvailable
        ? [
            this.showFocusTimeWidget,
            this.showMeetingsWidget,
            this.showUnusualActivityWidget,
            this.showWorkTimeClassificationWidget,
          ].filter(Boolean).length
        : 3;
    },
    showFocusTimeWidget() {
      return this.showWidget(INSIGHTS_CONSTANTS.api.focusTime, 'focusTime');
    },
    showMeetingsWidget() {
      return !this.canViewUnusualActivities && this.showWidget(INSIGHTS_CONSTANTS.api.meetings, 'meetings');
    },
    showUnusualActivityWidget() {
      return (
        this.canViewUnusualActivities && this.showWidget(INSIGHTS_CONSTANTS.api.unusualActivity.team, 'unusualActivity')
      );
    },
    showWorkTimeClassificationWidget() {
      return this.showWidget(INSIGHTS_CONSTANTS.api.workTimeClassification, 'workTimeClassification');
    },
  },
  methods: {
    insightsUrl(src) {
      if (this.showInsightsLink) {
        return this.url({
          tab: 'highlights',
          period: 'month',
          event: 'cta_dialog_viewed',
          src,
        });
      }

      return null;
    },
    openBenchmarksDialog() {
      if (window.analytics) {
        window.analytics.track('Activity benchmarks dialog link clicked');
      }

      this.showBenchmarksDialog = true;
      // Also dismiss the popover since the user has clicked btn.
      this.closeBenchmarksPopover();
    },
    closeBenchmarksDialog() {
      this.showBenchmarksDialog = false;
    },
    onHideBenchmarksPopover(setLocalStore = true) {
      if (this.showBenchmarksPopover && setLocalStore) {
        localStore.set('benchmarksDialog', { popoverShown: true });
      }
    },
    closeBenchmarksPopover() {
      const widget = this.$refs.widgetPopover;

      if (widget) {
        widget.close();
      }
    },
    widgetIsActive(widget) {
      return widget === 'meetings' ? this[widget]?.calendar_integration_status === 'synced' : this[widget] !== null;
    },
    showWidget(widgetKey, widget) {
      return !get(this.loading, widgetKey, false) && this.widgetIsActive(widget);
    },
    url(extraParams = {}) {
      let from;
      let to;

      if (extraParams.period === 'month') {
        from = formatInternalDate(moment(this.queryDate).startOf('month'));
        to = formatInternalDate(moment(from).endOf('month'));
      } else {
        from = this.queryDate;
        to = this.queryDate;
      }

      return InsightsResource.insightsLink(
        { organizationId: this.organization.id },
        { user_id: this.userId, from, to, time_zone: this.timeZone, ...extraParams },
      );
    },
  },
};
</script>
